import React, { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { MessageSquare, ArrowLeft, Send, Loader2, BellRing, BellOff, Inbox } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { useAuth } from "../../context/AuthContext";
import { enablePush, disablePush, getPushState } from "../../push";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtTime = (s) => { try { return new Date(s).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return s; } };

export const MessagesInbox = () => {
  const { token, user } = useAuth();
  const myEmail = (user?.email || "").toLowerCase();
  const [open, setOpen] = useState(false);
  const [threads, setThreads] = useState([]);
  const [unread, setUnread] = useState(0);
  const [active, setActive] = useState(null); // thread object
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [pushState, setPushState] = useState("off");
  const bottomRef = useRef(null);
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const loadUnread = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/messages/unread-count`, authHeader);
      setUnread(data.unread || 0);
    } catch (_) { /* silent */ }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadThreads = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/messages/threads`, authHeader);
      setThreads(data.threads || []);
    } catch (_) { /* silent */ }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMessages = useCallback(async (rid) => {
    const { data } = await axios.get(`${API}/messages/threads/${rid}`, authHeader);
    setMessages(data.messages || []);
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadUnread();
    const id = setInterval(loadUnread, 30000);
    return () => clearInterval(id);
  }, [loadUnread]);

  useEffect(() => { if (token) getPushState().then(setPushState); }, [token]);

  // Poll the open conversation for new messages
  useEffect(() => {
    if (!open || !active) return undefined;
    const id = setInterval(() => loadMessages(active.request_id).catch(() => {}), 15000);
    return () => clearInterval(id);
  }, [open, active, loadMessages]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const openThread = async (t) => {
    setActive(t);
    setMessages([]);
    try {
      await loadMessages(t.request_id);
      await axios.post(`${API}/messages/threads/${t.request_id}/read`, {}, authHeader);
      setUnread((u) => Math.max(0, u - (t.unread || 0)));
      setThreads((ts) => ts.map((x) => (x.request_id === t.request_id ? { ...x, unread: 0 } : x)));
    } catch (_) { toast.error("Could not load this conversation."); }
  };

  const send = async () => {
    const body = draft.trim();
    if (!body || !active) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages/threads/${active.request_id}`, { body }, authHeader);
      setDraft("");
      await loadMessages(active.request_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Message could not be sent.");
    } finally { setSending(false); }
  };

  const togglePush = async () => {
    if (pushState === "on") {
      const r = await disablePush(token);
      if (r.ok) { setPushState("off"); toast.success("Push notifications turned off on this device."); }
      return;
    }
    const r = await enablePush(token);
    if (r.ok) { setPushState("on"); toast.success("Push notifications enabled on this device."); }
    else if (r.reason === "ios-needs-install") toast.error("On iPhone/iPad: add this portal to your Home Screen first, then enable notifications inside the app.");
    else if (r.reason === "denied") toast.error("Notifications are blocked in your browser settings.");
    else toast.error("Push notifications are not supported in this browser.");
  };

  const openInbox = () => { setOpen(true); setActive(null); loadThreads(); };

  return (
    <>
      <button
        data-testid="messages-inbox-btn"
        onClick={openInbox}
        className="relative grid place-items-center w-11 h-11 rounded-full bg-[#EAF7FC] text-[#133955] hover:bg-[#54BCE6] transition-colors"
        aria-label="Messages"
        title="Messages"
      >
        <MessageSquare size={20} />
        {unread > 0 && (
          <span data-testid="messages-unread-badge" className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-[#9b1c1c] text-white text-[11px] font-bold grid place-items-center">
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setActive(null); loadUnread(); } }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col" data-testid="messages-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {active && (
                <button data-testid="thread-back" onClick={() => { setActive(null); loadThreads(); loadUnread(); }} className="grid place-items-center w-8 h-8 rounded-full hover:bg-[#EAF7FC] text-[#133955]" aria-label="Back to inbox">
                  <ArrowLeft size={16} />
                </button>
              )}
              <span className="min-w-0 truncate">{active ? `${active.other_name} · ${active.request_ref}` : "Messages"}</span>
            </DialogTitle>
          </DialogHeader>

          {!active && (
            <div className="mb-2 flex items-center justify-between gap-2 rounded-xl bg-[#EAF7FC] px-3 py-2">
              <span className="text-xs text-[#173042]/70" data-testid="push-state-label">
                {pushState === "on" ? "Push notifications on for this device"
                  : pushState === "ios-needs-install" ? "iPhone/iPad: add this portal to your Home Screen to enable push"
                  : pushState === "denied" ? "Notifications blocked in browser settings"
                  : pushState === "unsupported" ? "Push not supported in this browser"
                  : "Get push notifications for new messages"}
              </span>
              {(pushState === "off" || pushState === "on") && (
                <button data-testid="enable-push-btn" onClick={togglePush}
                  className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold transition-colors ${pushState === "on" ? "bg-white text-[#133955] border border-[rgba(19,57,85,0.15)]" : "bg-[#133955] text-white hover:bg-[#54BCE6] hover:text-[#133955]"}`}>
                  {pushState === "on" ? <><BellOff size={13} /> Turn Off</> : <><BellRing size={13} /> Enable</>}
                </button>
              )}
            </div>
          )}

          {active ? (
            <>
              <p className="text-xs text-[#173042]/60 -mt-1 truncate">{active.property_address}</p>
              <div className="flex-1 min-h-[240px] max-h-[46vh] overflow-y-auto space-y-2 pr-1" data-testid="message-list">
                {messages.length === 0 && <p className="text-sm text-[#173042]/55 text-center py-10">No messages yet — say hello.</p>}
                {messages.map((m) => {
                  const mine = (m.sender_email || "").toLowerCase() === myEmail;
                  return (
                    <div key={m.id} data-testid={`message-bubble-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 ${mine ? "bg-[#133955] text-white rounded-br-sm" : "bg-white border border-[rgba(19,57,85,0.10)] text-[#173042] rounded-bl-sm"}`}>
                        <p className="text-[11px] font-bold mb-0.5 text-[#54BCE6]">{m.sender_name}</p>
                        <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>
                        <p className={`text-[10px] mt-1 ${mine ? "text-white/60" : "text-[#173042]/45"}`}>{fmtTime(m.created_at)}</p>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-[rgba(19,57,85,0.08)]">
                <input
                  data-testid="message-input"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  placeholder="Type a message…"
                  maxLength={2000}
                  className="flex-1 rounded-full border border-[rgba(19,57,85,0.15)] bg-white px-4 py-2.5 text-sm text-[#173042] focus:outline-none focus:ring-2 focus:ring-[#54BCE6]"
                />
                <button data-testid="message-send-btn" onClick={send} disabled={sending || !draft.trim()}
                  className="grid place-items-center w-11 h-11 rounded-full bg-[#133955] text-white hover:bg-[#54BCE6] hover:text-[#133955] disabled:opacity-50 transition-colors" aria-label="Send message">
                  {sending ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} />}
                </button>
              </div>
            </>
          ) : (
            <div className="flex-1 overflow-y-auto space-y-2" data-testid="threads-list">
              {threads.length === 0 ? (
                <div className="text-center py-12" data-testid="no-threads">
                  <span className="grid place-items-center w-14 h-14 rounded-2xl bg-[#EAF7FC] text-[#54BCE6] mx-auto mb-4"><Inbox size={26} /></span>
                  <p className="text-[#173042] font-semibold">No conversations yet</p>
                  <p className="text-[#173042]/60 text-sm mt-1">A conversation opens here for each repair request once a contractor is assigned.</p>
                </div>
              ) : (
                threads.map((t) => (
                  <button key={t.request_id} data-testid={`thread-item-${t.request_id}`} onClick={() => openThread(t)}
                    className="w-full text-left rounded-xl border border-[rgba(19,57,85,0.08)] px-4 py-3 hover:border-[#54BCE6] transition-colors">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-[#133955] truncate">{t.other_name}</span>
                      <span className="font-mono text-xs font-bold text-[#54BCE6]">{t.request_ref}</span>
                      {t.unread > 0 && <span className="ml-auto min-w-[20px] h-5 px-1 rounded-full bg-[#9b1c1c] text-white text-[11px] font-bold grid place-items-center" data-testid={`thread-unread-${t.request_id}`}>{t.unread}</span>}
                    </div>
                    <p className="text-xs text-[#173042]/55 truncate mt-0.5">{t.property_address}</p>
                    {t.last_message && <p className="text-sm text-[#173042]/70 truncate mt-1">{t.last_message}</p>}
                  </button>
                ))
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
